// Shared store for the currently edited contentEditable text box so ribbon commands can act on the selection.
import { sanitizeHtml, clearParagraphStylesIn, SCALE_MIN, SCALE_MAX } from './textUtils';

export const editingStore = {
  el: null,
  objectId: null,
  sync: null,
  savedRange: null,
  wordCaret: null, // caret offset inside the word selected by selectWordAtCaret
  pendingCaret: null, // { objectId, offset }: where the caret goes when editing is handed over to another box of a linked story
  pendingPoint: null, // { objectId, x, y }: the double-click that started editing — the caret goes to that spot in the text
  appendHtml: null, // (fragmentHtml, midBlock, joiner, opts) => bool: append story words to the active editable (live pull-back between linked boxes)
};

/**
 * Put the caret at the text position under viewport point (x, y) inside the editable `el` (Publisher: double-clicking
 * into a text box puts the insertion point where you clicked, not at the end). Returns false when the point is not
 * over this editable's text (the caller then places the caret elsewhere).
 */
export const placeCaretAtPoint = (el, x, y) => {
  if (!el || typeof document === 'undefined') return false;
  let node = null, offset = 0;
  if (document.caretRangeFromPoint) {
    const r = document.caretRangeFromPoint(x, y);
    if (r) { node = r.startContainer; offset = r.startOffset; }
  } else if (document.caretPositionFromPoint) {
    const p = document.caretPositionFromPoint(x, y);
    if (p) { node = p.offsetNode; offset = p.offset; }
  }
  if (!node || !el.contains(node)) return false;
  const host = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
  if (host && host.closest('[data-wp-exclusion], [contenteditable="false"]')) return false; // a wrap-exclusion helper, not text
  const sel = window.getSelection();
  if (!sel) return false;
  const range = document.createRange();
  try { range.setStart(node, offset); } catch { return false; }
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
  return true;
};

/** Zero-width space: the placeholder character of an unused typing span (see "typing style at a bare caret"). */
export const ZWSP = '\u200B';

export const isEditingText = () => !!editingStore.el && document.body.contains(editingStore.el);

export const saveSelection = () => {
  const sel = window.getSelection();
  if (sel && sel.rangeCount > 0 && editingStore.el && editingStore.el.contains(sel.anchorNode)) {
    settleRootCaret();
    editingStore.savedRange = sel.getRangeAt(0).cloneRange();
  }
};

/**
 * A caret Chrome leaves on the editable's root between (or after) the paragraphs — a click on a very small blank line,
 * e.g. a 4 pt spacer, does that — is moved into the paragraph it is drawn at: the start of the block that follows, else
 * the end of the block before (its start when that block is blank). Typing, the size box and the blank-line rule then
 * all see the paragraph rather than the root, where typing would create bare text and the size box would read the
 * box's size — the tester's "it says 12 again when I click back into the line". Runs from saveSelection (every caret
 * move); a second selectionchange it causes finds the caret inside a block and does nothing.
 */
export const settleRootCaret = () => {
  const root = editingStore.el;
  const sel = window.getSelection();
  if (!root || !sel || !sel.rangeCount || !sel.isCollapsed || sel.anchorNode !== root) return false;
  const k = sel.anchorOffset;
  const isBlock = (n) => !!n && n.nodeType === Node.ELEMENT_NODE && PARA_BLOCK.test(n.tagName);
  const next = root.childNodes[k];
  const prev = root.childNodes[k - 1];
  const r = document.createRange();
  if (isBlock(next)) r.setStart(next, 0);
  else if (!next && isBlock(prev)) {
    if (isBlankBlock(prev)) r.setStart(prev, 0);
    else { r.selectNodeContents(prev); r.collapse(false); }
  } else return false; // bare text / inline content at the root: the caret is where it should be
  r.collapse(true);
  sel.removeAllRanges();
  sel.addRange(r);
  return true;
};

export const restoreSelection = () => {
  if (!editingStore.el) return;
  const live = window.getSelection();
  // The live selection is still inside the edited box (e.g. a key handler) → keep it; the remembered range may lag behind.
  if (live && live.rangeCount > 0 && editingStore.el.contains(live.anchorNode) && document.activeElement === editingStore.el) return;
  editingStore.el.focus({ preventScroll: true });
  if (editingStore.savedRange) {
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(editingStore.savedRange);
  }
};

/** The highlighted text while editing (live selection, else the remembered range) — for a dialog's sample line. */
export const selectedTextForSample = () => {
  if (!isEditingText()) return '';
  const r = currentRange();
  return r && !r.collapsed ? r.toString() : '';
};

export const hasTextSelection = () => {
  const sel = window.getSelection();
  if (sel && sel.rangeCount && !sel.isCollapsed && editingStore.el && editingStore.el.contains(sel.anchorNode)) return true;
  // Focus may sit in a task pane (Find & Replace) or a dialog while a match / highlight is remembered in the edited
  // box: the remembered range is still the user's text selection, so formatting applies to it rather than to the whole
  // box. Focus "in transit" (activeElement is the body for the instant between a blur and the next focus — a dialog
  // input committing its value as its Apply / OK button is pressed re-renders exactly then) counts the same way:
  // editing is still on, and deciding "nothing highlighted" at that moment sent Character Spacing to the whole box.
  const r = editingStore.savedRange;
  if (r && !r.collapsed && editingStore.el && editingStore.el.contains(r.commonAncestorContainer)) {
    const active = document.activeElement;
    if (!active || active === document.body) return true;
    return !!active.closest?.('[data-keep-editing], [data-ribbon]');
  }
  return false;
};

export const execEditCommand = (cmd, value = null, useCss = true) => {
  if (!isEditingText()) return false;
  restoreSelection();
  try {
    document.execCommand('styleWithCSS', false, useCss);
    document.execCommand(cmd, false, value);
  } catch {
    return false;
  }
  saveSelection();
  editingStore.sync?.();
  return true;
};

/**
 * Give the highlighted text inline CSS styles (font size, letter spacing, …) without disturbing the styles it already
 * has: the boundary text nodes are split, every text node inside the selection is wrapped in a span carrying `styles`
 * (a span that already wraps exactly that text just gets the properties), and the selection is kept.
 * (The former execCommand('fontSize') trick made the browser strip an existing font-size from a fully selected span,
 * so changing the spacing of a 36 px title silently reset it to the box size.)
 */
export const applyInlineStyle = (styles) => {
  if (!isEditingText()) return false;
  restoreSelection();
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount || sel.isCollapsed) return false;
  const root = editingStore.el;
  const range = sel.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) return false;
  // Split the boundary text nodes so the range covers whole text nodes. End first: splitting a text node moves the live
  // range's points past the split into the new node, so the start offset stays valid; splitting the start first (as
  // before) left the end pointing into the wrong node when both were the same text node — the range collapsed and a
  // size change on a highlighted word did nothing.
  const sc = range.startContainer, so = range.startOffset, ec = range.endContainer, eo = range.endOffset;
  if (ec.nodeType === Node.TEXT_NODE && eo > 0 && eo < ec.length) ec.splitText(eo);
  if (sc.nodeType === Node.TEXT_NODE && so > 0 && so < sc.length) range.setStart(sc.splitText(so), 0);
  const skip = (n) => !n.nodeValue.length || n.parentElement?.closest('[data-wp-tab], [data-wp-exclusion]');
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, { acceptNode: (n) => (range.intersectsNode(n) && !skip(n) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT) });
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  // intersectsNode also reports nodes touching the range at a boundary — keep only text that is really inside it
  const chosen = nodes.filter((n) => range.comparePoint(n, 0) === 0 && range.comparePoint(n, n.length) === 0);
  if (!chosen.length) return false;
  const wrapped = chosen.map((n) => {
    // a scaled word (Character Spacing ▸ Scaling) is styled from outside its `.wp-scale` marker, which stays a pure marker
    let node = n;
    if (isScaleSpan(n.parentElement) && n.parentElement.childNodes.length === 1) node = n.parentElement;
    const parent = node.parentElement;
    if (parent && parent !== root && parent.tagName === 'SPAN' && !isScaleSpan(parent) && parent.childNodes.length === 1) { Object.assign(parent.style, styles); return parent; }
    const span = document.createElement('span');
    Object.assign(span.style, styles);
    parent.insertBefore(span, node);
    span.appendChild(node);
    return span;
  });
  const r2 = document.createRange();
  r2.setStartBefore(wrapped[0]);
  r2.setEndAfter(wrapped[wrapped.length - 1]);
  sel.removeAllRanges();
  sel.addRange(r2);
  saveSelection();
  editingStore.sync?.();
  return true;
};

/* ---------- Character Spacing ▸ Scaling on highlighted text ---------- */
/*
 * A horizontally scaled word is <span class="wp-scale" style="--wp-sx: 0.8">word</span> (index.css turns the factor into
 * a narrower / wider font-size whose glyphs are stretched back to full height). Every word is its own span — the spaces
 * between them stay outside — so the text still wraps word by word.
 */
export const SCALE_CLASS = 'wp-scale';
const SCALE_SEL = `.${SCALE_CLASS}`;
const SCALE_PROP = '--wp-sx';
const WS = /[\s\u200B]/;
const isScaleSpan = (n) => !!n && n.nodeType === Node.ELEMENT_NODE && n.classList.contains(SCALE_CLASS);
/** Scale factor (1 = 100 %) of a `.wp-scale` span. */
export const scaleOfSpan = (span) => { const v = parseFloat(span && span.style ? span.style.getPropertyValue(SCALE_PROP) : ''); return Number.isFinite(v) && v > 0 ? v : 1; };
const makeScaleSpan = (factor) => { const s = document.createElement('span'); s.className = SCALE_CLASS; s.style.setProperty(SCALE_PROP, String(factor)); return s; };
/** (node, offset) → character offset within root's text; the inverse below. Text is never changed by the surgery here, so offsets survive it. */
const textOffsetIn = (root, node, offset) => { const r = document.createRange(); r.setStart(root, 0); r.setEnd(node, offset); return r.toString().length; };
const pointAtTextOffset = (root, k) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let acc = 0, last = null;
  while (walker.nextNode()) {
    const n = walker.currentNode, len = n.nodeValue.length;
    if (acc + len >= k) return { node: n, offset: k - acc };
    acc += len; last = n;
  }
  return last ? { node: last, offset: last.nodeValue.length } : { node: root, offset: 0 };
};
const unwrapSpan = (span) => { const parent = span.parentNode; if (!parent) return null; while (span.firstChild) parent.insertBefore(span.firstChild, span); parent.removeChild(span); return parent; };

/**
 * Scale the highlighted text horizontally by `factor` (1 = the box's own width; the Character Spacing dialog passes
 * wanted % / box %). Scaled words the highlight touches are rewrapped at the new factor; factor 1 just frees them.
 * Keeps the highlight. Returns false when nothing is highlighted.
 */
export const applyInlineScale = (factor) => {
  if (!isEditingText()) return false;
  restoreSelection();
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount || sel.isCollapsed) return false;
  const root = editingStore.el;
  let range = sel.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) return false;
  const f = Math.round(Math.min(SCALE_MAX, Math.max(SCALE_MIN, Number(factor) || 1)) * 1000) / 1000;
  // the highlight as text offsets — the node surgery below would throw the live range off
  const a = textOffsetIn(root, range.startContainer, range.startOffset);
  const b = textOffsetIn(root, range.endContainer, range.endOffset);
  // 1. scaled words the highlight touches lose their wrapper (rewrapped below at the new factor, or freed)
  const touched = Array.from(root.querySelectorAll(SCALE_SEL)).filter((s) => range.intersectsNode(s));
  const parents = new Set();
  touched.forEach((s) => { const p = unwrapSpan(s); if (p) parents.add(p); });
  parents.forEach((p) => p.normalize()); // "Gr" + "ace" is one word again
  // 2. the highlight, rebuilt
  const p1 = pointAtTextOffset(root, a), p2 = pointAtTextOffset(root, b);
  range = document.createRange();
  range.setStart(p1.node, p1.offset);
  range.setEnd(p2.node, p2.offset);
  const wrapped = [];
  if (Math.abs(f - 1) >= 0.0005) {
    // 3. boundary text nodes split so the range covers whole text nodes (end first — see applyInlineStyle)
    const sc = range.startContainer, so = range.startOffset, ec = range.endContainer, eo = range.endOffset;
    if (ec.nodeType === Node.TEXT_NODE && eo > 0 && eo < ec.length) ec.splitText(eo);
    if (sc.nodeType === Node.TEXT_NODE && so > 0 && so < sc.length) range.setStart(sc.splitText(so), 0);
    const skip = (n) => !n.nodeValue.length || n.parentElement?.closest('[data-wp-tab], [data-wp-exclusion]');
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, { acceptNode: (n) => (range.intersectsNode(n) && !skip(n) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT) });
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    const chosen = nodes.filter((n) => range.comparePoint(n, 0) === 0 && range.comparePoint(n, n.length) === 0);
    // 4. every word of the chosen text becomes a scaled span; whitespace stays between them
    chosen.forEach((n) => {
      let node = n;
      const parts = node.nodeValue.split(/([\s\u200B]+)/).filter((s) => s.length);
      parts.forEach((part, i) => {
        const rest = i < parts.length - 1 ? node.splitText(part.length) : null;
        if (!WS.test(part)) {
          const span = makeScaleSpan(f);
          node.parentNode.insertBefore(span, node);
          span.appendChild(node);
          wrapped.push(span);
        }
        node = rest;
      });
    });
  }
  const r2 = document.createRange();
  if (wrapped.length) { r2.setStartBefore(wrapped[0]); r2.setEndAfter(wrapped[wrapped.length - 1]); }
  else { r2.setStart(range.startContainer, range.startOffset); r2.setEnd(range.endContainer, range.endOffset); }
  sel.removeAllRanges();
  sel.addRange(r2);
  saveSelection();
  editingStore.sync?.();
  return true;
};

/**
 * Typing inside a scaled word: a space (or a word pasted) makes one span hold two words, which could then not wrap
 * between them — the span is split around the whitespace, same factor on both sides, caret kept. A space just typed at
 * the end of a scaled word waits for the next letter (so the new word continues scaled, as it does in Publisher).
 * Spans left empty (the word was deleted) go. Runs on every input; returns true when anything changed.
 */
export const normalizeScaleSpans = (el, { final = false } = {}) => {
  if (!el || !el.querySelector(SCALE_SEL)) return false;
  const sel = window.getSelection();
  const caret = !final && sel && sel.rangeCount && sel.isCollapsed && el.contains(sel.anchorNode) ? textOffsetIn(el, sel.anchorNode, sel.anchorOffset) : null;
  let changed = false;
  el.querySelectorAll(SCALE_SEL).forEach((span) => {
    if (!span.isConnected) return;
    if (!span.textContent.length) { span.remove(); changed = true; return; }
    for (let guard = 0; guard < 50 && span.isConnected; guard += 1) {
      const text = span.textContent;
      if (!WS.test(text)) break;
      // the first whitespace run inside the span
      const walker = document.createTreeWalker(span, NodeFilter.SHOW_TEXT);
      let node = null, m = null;
      while (walker.nextNode()) { const t = walker.currentNode; const mm = /[\s\u200B]+/.exec(t.nodeValue); if (mm) { node = t; m = mm; break; } }
      if (!node) break;
      const atEnd = textOffsetIn(span, node, m.index + m[0].length) === text.length;
      if (atEnd && !final && caret !== null && caret === textOffsetIn(el, span, span.childNodes.length)) break; // just typed, caret after it: wait
      const before = document.createRange(); before.setStart(span, 0); before.setEnd(node, m.index);
      const left = before.extractContents();
      const wsRange = document.createRange(); wsRange.setStart(node, 0); wsRange.setEnd(node, m[0].length);
      const ws = wsRange.extractContents();
      const parent = span.parentNode;
      if (left.textContent.length) { const l = span.cloneNode(false); l.appendChild(left); parent.insertBefore(l, span); }
      parent.insertBefore(ws, span);
      changed = true;
      if (!span.textContent.length) { span.remove(); break; }
    }
  });
  if (changed && caret !== null) {
    const p = pointAtTextOffset(el, caret);
    const r = document.createRange(); r.setStart(p.node, p.offset); r.collapse(true);
    sel.removeAllRanges(); sel.addRange(r);
    saveSelection();
  }
  return changed;
};

/* ---------- The highlight stays visible while the user works in a dialog / the ribbon ---------- */
const HL_NAME = 'wp-selection';
const highlightsApi = () => (typeof window !== 'undefined' && window.CSS && window.CSS.highlights && typeof window.Highlight === 'function' ? window.CSS.highlights : null);
/** Paint the remembered highlight (focus has left the editable) with the Custom Highlight API; false where unsupported / nothing to show. */
export const showRememberedSelection = () => {
  const api = highlightsApi();
  const r = editingStore.savedRange;
  if (!api || !r || r.collapsed || !editingStore.el || !editingStore.el.contains(r.commonAncestorContainer)) return false;
  api.set(HL_NAME, new window.Highlight(r.cloneRange()));
  return true;
};
export const hideRememberedSelection = () => { const api = highlightsApi(); if (api) api.delete(HL_NAME); };
export const rememberedSelectionShown = () => { const api = highlightsApi(); return !!api && api.has(HL_NAME); };

/** The user's text range: the live selection when it is inside the edited box, else the remembered one. */
const currentRange = () => {
  const sel = window.getSelection();
  if (sel && sel.rangeCount && editingStore.el && editingStore.el.contains(sel.anchorNode)) return sel.getRangeAt(0);
  return editingStore.savedRange;
};

/** Nearest inline element from the range's start up to the editable root that satisfies `pick` (returns its value). */
const inlineValueAtStart = (pick) => {
  const r = currentRange();
  let node = r ? r.startContainer : null;
  // a range starting at an element position (e.g. right before the span a style was just applied to) → its first node
  if (node && node.nodeType === Node.ELEMENT_NODE && node.childNodes[r.startOffset]) node = node.childNodes[r.startOffset];
  if (node && node.nodeType === Node.TEXT_NODE) node = node.parentElement;
  while (node && node !== editingStore.el && editingStore.el?.contains(node)) {
    const v = pick(node);
    if (v != null) return v;
    node = node.parentElement;
  }
  return undefined;
};

/**
 * Font size (box px) of the text at the selection's start: the nearest inline `font-size` span, else `fallback` (the
 * box's own size). Reads inline styles rather than computed ones, so it is independent of the editable's CSS zoom.
 */
export const selectionFontSize = (fallback = 16) => {
  if (!isEditingText()) return fallback;
  const v = inlineValueAtStart((node) => {
    const fs = parseFloat(node.style?.fontSize);
    if (Number.isFinite(fs) && fs > 0 && /px$/.test(node.style.fontSize)) return fs;
    if (node.tagName === 'FONT' && node.getAttribute('size')) { const map = { 1: 10, 2: 13, 3: 16, 4: 18, 5: 24, 6: 32, 7: 48 }; const s = map[node.getAttribute('size')]; if (s) return s; }
    return undefined;
  });
  return v ?? fallback;
};

/**
 * Font family of the text at the selection's start: the nearest inline `font-family` span / `<font face>`, else
 * `fallback` (the box's own family). Lets the ribbon show the font under the caret in a box that mixes several.
 */
const firstFamily = (ff) => String(ff || '').split(',')[0].trim().replace(/^["']|["']$/g, '');

export const selectionFontFamily = (fallback) => {
  if (!isEditingText()) return fallback;
  const v = inlineValueAtStart((node) => {
    const ff = firstFamily(node.style?.fontFamily);
    if (ff) return ff;
    const face = node.tagName === 'FONT' ? node.getAttribute('face') : null;
    if (face) return firstFamily(face);
    return undefined;
  });
  if (v) return v;
  // A bare caret may carry a "typing style" (font picked, nothing typed yet): the browser reports it here.
  const r = currentRange();
  if (r && r.collapsed && document.activeElement === editingStore.el) {
    try { const q = firstFamily(document.queryCommandValue('fontName')); if (q) return q; } catch { /* ignore */ }
  }
  return fallback;
};

const WORD_CHAR = /[\p{L}\p{N}\p{M}'’_-]/u;

const rgbToHex = (c) => {
  const m = String(c || '').match(/\d+(\.\d+)?/g);
  if (!m || m.length < 3) return /^#[0-9a-f]{6}$/i.test(c) ? c : null;
  return `#${m.slice(0, 3).map((v) => Math.round(+v).toString(16).padStart(2, '0')).join('')}`;
};

/**
 * Colour of the text at the selection's start: the nearest inline `color` span / `<font color>`, else the browser's
 * typing colour at a bare caret, else `fallback` (the box's own colour). The ribbon swatch shows this while editing —
 * colouring highlighted text changes the span, not the box, so the box colour alone would keep showing the old one.
 */
export const selectionColor = (fallback) => {
  if (!isEditingText()) return fallback;
  const v = inlineValueAtStart((node) => {
    const c = rgbToHex(node.style?.color);
    if (c) return c;
    const attr = node.tagName === 'FONT' ? node.getAttribute('color') : null;
    if (attr) return rgbToHex(attr) || attr;
    return undefined;
  });
  if (v) return v;
  const r = currentRange();
  if (r && r.collapsed && document.activeElement === editingStore.el) {
    try { const q = rgbToHex(document.queryCommandValue('foreColor')); if (q) return q; } catch { /* ignore */ }
  }
  return fallback;
};

/**
 * Character formatting of the text at the caret / selection start while editing — what the Font dialog starts from and
 * writes back, so opening it on highlighted text and pressing OK changes nothing. Inline (nearest span) values first,
 * the browser's state for bold / italic / underline / colour, `box` props for the rest.
 */
export const selectionCharacterStyle = (box = {}) => {
  if (!isEditingText()) return {};
  const inline = (prop) => inlineValueAtStart((node) => node.style?.[prop] || undefined);
  const out = {};
  const ff = selectionFontFamily(box.fontFamily);
  if (ff) out.fontFamily = ff;
  out.fontSize = selectionFontSize(box.fontSize || 16);
  // the browser's state commands read the live selection — only meaningful while it is still inside the box
  const live = window.getSelection();
  const liveInBox = !!(live && live.rangeCount && editingStore.el.contains(live.anchorNode));
  if (liveInBox) {
    try {
      out.fontWeight = document.queryCommandState('bold') ? 'bold' : 'normal';
      out.fontStyle = document.queryCommandState('italic') ? 'italic' : 'normal';
      const dec = [];
      if (document.queryCommandState('underline')) dec.push('underline');
      if (document.queryCommandState('strikeThrough')) dec.push('line-through');
      out.textDecoration = dec.length ? dec.join(' ') : 'none';
      const c = rgbToHex(document.queryCommandValue('foreColor'));
      if (c) out.color = c;
      out.fontVariantPosition = document.queryCommandState('superscript') ? 'super' : document.queryCommandState('subscript') ? 'sub' : 'normal';
    } catch { /* ignore */ }
  }
  const tt = inline('textTransform');
  if (tt) out.textTransform = tt;
  const fv = inline('fontVariant');
  if (fv) out.fontVariant = fv;
  // Character Spacing ▸ Scaling of the text at the start: the box's own scale × the word's inline factor (100 % when none)
  const sx = inlineValueAtStart((node) => (isScaleSpan(node) ? scaleOfSpan(node) : undefined));
  out.textScale = Math.round((box.textScale || 100) * (sx || 1));
  const fk = inline('fontKerning');
  if (fk) out.kerning = fk !== 'none';
  return out;
};

/**
 * Nothing highlighted while editing: when the caret sits *inside* a word, select that word so a formatting command
 * applies to it (what Publisher / Word do) instead of to the whole box. Returns true when a word was selected; the
 * caret's offset inside the word is remembered so `restoreCaretInWord` can put it back afterwards.
 */
export const selectWordAtCaret = () => {
  if (!isEditingText()) return false;
  restoreSelection();
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount || !sel.isCollapsed) return false;
  const r = sel.getRangeAt(0);
  const node = r.startContainer;
  if (node.nodeType !== Node.TEXT_NODE || !editingStore.el.contains(node)) return false;
  const text = node.data || '';
  const off = r.startOffset;
  const isWord = (ch) => !!ch && WORD_CHAR.test(ch) && ch !== ZWSP;
  if (!isWord(text[off - 1]) || !isWord(text[off])) return false;
  let s = off;
  let e = off;
  while (s > 0 && isWord(text[s - 1])) s -= 1;
  while (e < text.length && isWord(text[e])) e += 1;
  const range = document.createRange();
  range.setStart(node, s);
  range.setEnd(node, e);
  sel.removeAllRanges();
  sel.addRange(range);
  editingStore.wordCaret = off - s;
  saveSelection();
  return true;
};

/**
 * After formatting the word under the caret: collapse the (re-wrapped) word selection back to where the caret was inside
 * the word, so typing carries on in the word — now in its new style.
 */
export const restoreCaretInWord = () => {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount || !editingStore.el || !editingStore.el.contains(sel.anchorNode)) return;
  if (sel.isCollapsed) { saveSelection(); return; }
  const range = sel.getRangeAt(0);
  let left = editingStore.wordCaret ?? range.toString().length;
  const cac = range.commonAncestorContainer;
  const nodes = [];
  if (cac.nodeType === Node.TEXT_NODE) nodes.push(cac);
  else {
    const walker = document.createTreeWalker(cac, NodeFilter.SHOW_TEXT, { acceptNode: (n) => (range.intersectsNode(n) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT) });
    let n;
    while ((n = walker.nextNode())) nodes.push(n);
  }
  let target = null;
  for (const n of nodes) {
    const from = n === range.startContainer ? range.startOffset : 0;
    const to = n === range.endContainer ? range.endOffset : n.length;
    const len = Math.max(0, to - from);
    if (left <= len) { target = [n, from + left]; break; }
    left -= len;
  }
  if (target) { const r2 = document.createRange(); r2.setStart(target[0], target[1]); r2.collapse(true); sel.removeAllRanges(); sel.addRange(r2); }
  else sel.collapseToEnd();
  saveSelection();
};

/** True when the edited box holds any visible text (an empty box takes box-level formatting, not a typing style). */
export const editedBoxHasText = () => !!editingStore.el && /[^\s\u200B]/.test(editingStore.el.textContent || '');

/* ---------------- paragraph formatting of the highlighted paragraphs ---------------- */
// Publisher: line spacing, space before / after, indents and alignment are *paragraph* properties — Home ▸ Line Spacing
// on highlighted text changes the paragraphs the highlight touches only (a partly highlighted paragraph changes as a
// whole), never the other paragraphs of the box and never other boxes. They live as inline styles on the paragraph
// blocks (<div> / <p> / <li>), which sanitizeHtml keeps and the display, the measurement and the layout engine all
// honour; a paragraph without its own value keeps using the box's. With nothing highlighted the whole box changes
// (useTextCommands.applyParagraphProps: box property + clearEditedParagraphStyles).
const PARA_BLOCK = /^(DIV|P|LI)$/;

const paragraphBlockOf = (node) => {
  const root = editingStore.el;
  let n = node && node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
  while (n && n !== root && root?.contains(n) && !PARA_BLOCK.test(n.tagName)) n = n.parentElement;
  return n && n !== root && root?.contains(n) ? n : null;
};

/* ---------------- blank lines: the paragraph mark's size ---------------- */
// Publisher / Word: an empty paragraph is as tall as its invisible paragraph mark, so the font size box resizes a blank
// line — a 6 pt blank line is a thin spacer, a 36 pt one a big gap. The size lives as `font-size` on the paragraph block
// (what the <br> inside it renders at; sanitizeHtml keeps it, the display, the measurement, the layout engine and the
// ribbon's size box all read it) and text typed into the line later inherits it.
const isBlankBlock = (b) => !!b && !/[^\s\u200B]/.test(b.textContent || '') && !b.querySelector('img, table');

/**
 * The caret's paragraph when it is a blank line in a box that holds other content (another paragraph or any text);
 * null for a highlight, a line with text, or a box that is nothing but one empty paragraph (that one keeps taking the
 * box-level size, which gives the same result and is what typing into a fresh box expects).
 */
export const caretBlankLine = () => {
  if (!isEditingText()) return null;
  const r = currentRange();
  if (!r || !r.collapsed) return null;
  const root = editingStore.el;
  if (!root.contains(r.startContainer)) return null;
  const b = paragraphBlockOf(r.startContainer);
  if (!isBlankBlock(b)) return null;
  const others = Array.from(root.querySelectorAll('div, p, li')).some((x) => x !== b && !x.contains(b) && !b.contains(x));
  return others || editedBoxHasText() ? b : null;
};

/** Font size (box px) of the caret's blank line → the line's own height. Returns false when the caret is not on one. */
export const setBlankLineFontSize = (px) => {
  if (!isEditingText()) return false;
  restoreSelection();
  settleRootCaret(); // a caret left on the root next to the blank paragraph belongs to that paragraph
  saveSelection();
  const b = caretBlankLine();
  if (b) {
    b.style.fontSize = `${px}px`;
    // Inline wrappers inside the blank line must not override its size: a typing span left in the line (a size picked
    // here before this rule existed), or the copy of the previous line's span Chrome puts around the <br> when Enter is
    // pressed at the end of sized text (<div><span style="font-size: 16px"><br></span></div>) — that one kept the line
    // at its old height and made the size box read the old size again once the caret came back.
    b.querySelectorAll('*').forEach((s) => {
      if (s.hasAttribute('data-wp-tab')) return;
      s.style.removeProperty('font-size');
      if (s.tagName === 'FONT') s.removeAttribute('size');
      if (!s.getAttribute('style')) s.removeAttribute('style');
    });
    saveSelection();
    editingStore.sync?.();
    return true;
  }
  const soft = caretSoftBlankLine();
  if (!soft) return false;
  const blank = splitSoftBlankLine(soft.block, soft, px);
  const r = document.createRange();
  r.setStart(blank, 0);
  r.collapse(true);
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(r);
  saveSelection();
  editingStore.sync?.();
  return true;
};

/* ---------------- blank lines made with Shift+Enter (soft returns) ---------------- */
// The editable lays text out with `white-space: pre-wrap`, so Shift+Enter — Publisher's line break — inserts a newline
// *character* inside the paragraph rather than a paragraph of its own. A blank line made that way is an empty stretch
// between two newlines of a block that holds text elsewhere. Every CSS line box of a block is at least as tall as the
// block's own font (the strut), so that line cannot be made smaller in place: the size box turns it into a paragraph
// of its own — head text / blank paragraph / tail text, the paragraph's own styles (alignment, line spacing…) copied
// to each — which is what such a line is in Publisher's terms anyway. Without this the size only reached the caret's
// typing style: the cursor shrank, the line did not, and the size came back as the paragraph's once the caret moved.

/** The block's line-forming items in order: text nodes (their data) and <br>s (a newline). */
const blockLineItems = (b) => {
  const w = document.createTreeWalker(b, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  const out = [];
  let n;
  while ((n = w.nextNode())) {
    if (n.nodeType === Node.TEXT_NODE) out.push({ node: n, text: n.data });
    else if (n.tagName === 'BR') out.push({ node: n, text: '\n', br: true });
  }
  return out;
};
const holdsVisible = (el) => !!el && (/[^\s\u200B]/.test(el.textContent || '') || !!el.querySelector('br, img'));

/**
 * The caret's blank soft line: `{ block, items, text, ls, le }` — the block, its line items (text nodes and <br>s),
 * their joined text and the [ls, le) stretch of that text the caret's line covers (whitespace only, bounded by line
 * breaks or the block's ends). null when the caret is not on such a line (a highlight, a line with text, a block
 * without line breaks, a blank block — that one is `caretBlankLine`).
 */
export const caretSoftBlankLine = () => {
  if (!isEditingText()) return null;
  const r = currentRange();
  if (!r || !r.collapsed) return null;
  const root = editingStore.el;
  if (!root.contains(r.startContainer)) return null;
  const block = paragraphBlockOf(r.startContainer);
  if (!block || isBlankBlock(block)) return null;
  const items = blockLineItems(block);
  if (!items.length) return null;
  const text = items.map((it) => it.text).join('');
  if (!text.includes('\n')) return null;
  // the caret's offset in that text
  let c = 0;
  if (r.startContainer.nodeType === Node.TEXT_NODE) {
    let found = false;
    for (const it of items) { if (it.node === r.startContainer) { c += r.startOffset; found = true; break; } c += it.text.length; }
    if (!found) return null;
  } else {
    const probe = document.createRange();
    for (const it of items) {
      probe.setStartBefore(it.node);
      probe.collapse(true);
      if (r.compareBoundaryPoints(Range.START_TO_START, probe) <= 0) break; // the item starts at or after the caret
      c += it.text.length;
    }
  }
  const ls = c === 0 ? 0 : text.lastIndexOf('\n', c - 1) + 1;
  const nl = text.indexOf('\n', c);
  const le = nl === -1 ? text.length : nl;
  if (ls === 0 && le === text.length) return null;
  if (/[^\s\u200B]/.test(text.slice(ls, le))) return null;
  // an empty stretch after a trailing <br> is not a line of its own (Chrome's placeholder break at a block's end)
  if (ls === text.length && items[items.length - 1].br) return null;
  return { block, items, text, ls, le };
};

/**
 * Turn the blank soft line `{ ls, le }` of `block` into a blank paragraph of its own (font size `px`), the text before
 * it staying in `block`, the text after it moving to a clone of the block. Returns the new blank paragraph.
 */
export const splitSoftBlankLine = (block, { items, text, ls, le }, px) => {
  const pos = (i) => {
    let acc = 0;
    for (const it of items) {
      if (it.br) {
        const parent = it.node.parentNode;
        const idx = Array.prototype.indexOf.call(parent.childNodes, it.node);
        if (i === acc) return [parent, idx];
        if (i === acc + 1) return [parent, idx + 1];
      } else if (i <= acc + it.text.length) return [it.node, i - acc];
      acc += it.text.length;
    }
    return [block, block.childNodes.length];
  };
  const tidy = (el) => {
    el.normalize();
    Array.from(el.querySelectorAll('*')).reverse().forEach((e) => { if (!e.hasAttribute('data-wp-tab') && !e.textContent && !e.querySelector('br, img')) e.remove(); });
  };
  let tailBlock = null;
  if (le < text.length) {
    // the text after the blank line, from the line break that ends it to the block's end
    const r = document.createRange();
    const [n, o] = pos(le);
    r.setStart(n, o);
    r.setEnd(block, block.childNodes.length);
    const frag = r.extractContents();
    // drop that line break (the fragment's first item)
    const w = document.createTreeWalker(frag, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
    let t;
    while ((t = w.nextNode())) {
      if (t.nodeType === Node.TEXT_NODE) { if (!t.data.length) continue; if (t.data[0] === '\n') t.data = t.data.slice(1); break; }
      if (t.tagName === 'BR') { t.remove(); break; }
    }
    tailBlock = block.cloneNode(false);
    tailBlock.appendChild(frag);
    tidy(tailBlock);
    if (!holdsVisible(tailBlock)) tailBlock = null;
  }
  // the blank stretch itself, with the line break that started it
  const r2 = document.createRange();
  const [n2, o2] = pos(ls > 0 ? ls - 1 : 0);
  r2.setStart(n2, o2);
  r2.setEnd(block, block.childNodes.length);
  r2.deleteContents();
  tidy(block);
  const blank = block.cloneNode(false);
  blank.innerHTML = '<br>';
  blank.style.fontSize = `${px}px`;
  block.after(blank);
  if (tailBlock) blank.after(tailBlock);
  if (!holdsVisible(block)) block.remove();
  return blank;
};

/**
 * The paragraph blocks the current selection touches, in document order: the caret's paragraph when nothing is
 * highlighted; for a highlight, every innermost block it intersects — a block the highlight only reaches the *start*
 * of (the selection ends right before its first character, as after a triple-click) is not included. Bare text at
 * the editable's root is wrapped into a paragraph first so it can carry paragraph formatting.
 */
export const selectedParagraphs = ({ wrap = true } = {}) => {
  if (!isEditingText()) return [];
  const r = currentRange();
  const root = editingStore.el;
  if (!r || !root.contains(r.commonAncestorContainer)) return [];
  const inner = (b) => !b.querySelector('div, p, li') && !b.closest('[data-wp-exclusion]');
  let blocks = Array.from(root.querySelectorAll('div, p, li')).filter(inner);
  if (!blocks.length) {
    if (!wrap) return []; // read-only callers (ribbon state) never change the DOM
    // no paragraph element at all (plain inline content): give the text one
    const div = document.createElement('div');
    Array.from(root.childNodes).forEach((n) => { if (!(n.nodeType === Node.ELEMENT_NODE && n.hasAttribute('data-wp-exclusion'))) div.appendChild(n); });
    root.appendChild(div);
    blocks = [div];
  }
  if (r.collapsed) { const b = paragraphBlockOf(r.startContainer); return b ? [b] : []; }
  const hit = blocks.filter((b) => r.intersectsNode(b));
  if (hit.length <= 1) return hit;
  const last = hit[hit.length - 1];
  // the highlight merely reaches the *start* of the last block (a triple-click extends to the next paragraph's first
  // character): no character of it is highlighted, so it is not part of the selection — unless it is an empty
  // paragraph (just a <br>) the highlight runs through
  const pre = document.createRange();
  pre.setStart(last, 0);
  pre.setEnd(r.endContainer, r.endOffset);
  if (pre.toString().length === 0 && !pre.cloneContents().querySelector('br') && last !== paragraphBlockOf(r.startContainer)) hit.pop();
  return hit;
};

const kebab = (k) => k.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);

/**
 * Put `styles` (camelCase CSS → value; '' / null removes the property) on the paragraphs under the selection — see
 * selectedParagraphs (low level: a bare caret addresses its own paragraph; the "nothing highlighted → whole box" rule
 * is useTextCommands.applyParagraphProps'). Returns false when the box is not being edited or no paragraph is targeted.
 */
export const applyParagraphStyle = (styles) => {
  if (!isEditingText()) return false;
  restoreSelection();
  const blocks = selectedParagraphs();
  if (!blocks.length) return false;
  blocks.forEach((b) => {
    Object.entries(styles).forEach(([k, v]) => {
      if (v === '' || v === null || v === undefined) b.style.removeProperty(kebab(k));
      else b.style[k] = String(v);
    });
    if (!b.getAttribute('style')) b.removeAttribute('style');
  });
  saveSelection();
  editingStore.sync?.();
  return true;
};

/**
 * Whole-box paragraph formatting while editing: drop the paragraphs' own values of the given props (`lineHeight`,
 * `spaceBefore`, …) from the live editable so the box property the caller sets next really reaches every paragraph.
 */
export const clearEditedParagraphStyles = (keys) => {
  if (!isEditingText()) return false;
  if (!clearParagraphStylesIn(editingStore.el, keys)) return false;
  editingStore.sync?.();
  return true;
};

/**
 * Paragraph formatting of the first highlighted paragraph (the caret's) as props: { textAlign, lineHeight,
 * spaceBefore, spaceAfter, indentLeft, firstLineIndent } — only the values the paragraph sets itself (the rest is
 * the box's). Line height is the unitless multiplier the box uses too.
 */
export const selectionParagraphStyle = () => {
  const b = selectedParagraphs({ wrap: false })[0];
  if (!b) return {};
  const out = {};
  const st = b.style;
  if (st.textAlign) out.textAlign = st.textAlign;
  if (st.lineHeight && st.lineHeight !== 'normal') {
    const v = parseFloat(st.lineHeight);
    // a px line height is an exact height ("Exactly N pt", lib/units) and stays one; a bare number is a multiple
    if (Number.isFinite(v) && v > 0) out.lineHeight = /px$/.test(st.lineHeight) ? `${v}px` : v;
  }
  const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : undefined; };
  const mt = num(st.marginTop); if (mt !== undefined) out.spaceBefore = mt;
  const mb = num(st.marginBottom); if (mb !== undefined) out.spaceAfter = mb;
  const ml = num(st.marginLeft || st.paddingLeft); if (ml !== undefined) out.indentLeft = ml;
  const ti = num(st.textIndent); if (ti !== undefined) out.firstLineIndent = ti;
  return out;
};

/** Props patch → inline paragraph styles (undefined keys are left alone, null / 0-where-default removes). */
export const paragraphPropsToStyle = (patch) => {
  const css = {};
  if ('textAlign' in patch) css.textAlign = patch.textAlign || '';
  if ('lineHeight' in patch) css.lineHeight = !patch.lineHeight ? '' : typeof patch.lineHeight === 'string' ? patch.lineHeight : String(Math.round(patch.lineHeight * 1000) / 1000);
  if ('spaceBefore' in patch) css.marginTop = patch.spaceBefore ? `${patch.spaceBefore}px` : '';
  if ('spaceAfter' in patch) css.marginBottom = patch.spaceAfter ? `${patch.spaceAfter}px` : '';
  if ('indentLeft' in patch) css.marginLeft = patch.indentLeft ? `${patch.indentLeft}px` : '';
  if ('firstLineIndent' in patch) css.textIndent = patch.firstLineIndent ? `${patch.firstLineIndent}px` : '';
  return css;
};

/* ---------------- Format Painter (text) ---------------- */
// Publisher / Word: highlight text, click Format Painter, then highlight the text that should look the same.
// `captureTextFormat` reads the character formatting at the highlight's start (the caret's when nothing is highlighted)
// plus its paragraph's formatting; `paintTextFormat` puts them on the new highlight — a bare click paints nothing, the
// painter stays armed until text is highlighted.

/** The formatting under the current highlight: { char: {...character props}, para: {...paragraph props} }. */
export const captureTextFormat = (box = {}) => {
  if (!isEditingText()) return null;
  const char = selectionCharacterStyle(box);
  // inline letter-spacing / highlight colour at the start of the selection (the browser has no state command for them)
  const ls = inlineValueAtStart((node) => node.style?.letterSpacing || undefined);
  if (ls) char.letterSpacing = ls;
  const bg = inlineValueAtStart((node) => (node.style?.backgroundColor && node.style.backgroundColor !== 'transparent' ? node.style.backgroundColor : undefined));
  if (bg) char.backgroundColor = bg;
  const para = selectionParagraphStyle();
  // the box's own paragraph values fill the gaps, so painting into another box reproduces the whole look
  ['textAlign', 'lineHeight', 'spaceBefore', 'spaceAfter', 'indentLeft', 'firstLineIndent'].forEach((k) => { if (para[k] === undefined && box[k] !== undefined) para[k] = box[k]; });
  return { char, para };
};

/**
 * Apply a captured text format to the highlighted text. Character formatting goes on as inline styles; bold / italic /
 * underline / strikethrough / sub- and superscript through the browser's toggles so they also come *off* where the
 * source has them off. Paragraph formatting goes on the paragraphs the highlight touches. Nothing highlighted → false.
 */
export const paintTextFormat = (fmt) => {
  if (!fmt || !isEditingText()) return false;
  restoreSelection();
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount || sel.isCollapsed || !editingStore.el.contains(sel.anchorNode)) return false;
  const { char = {}, para = {} } = fmt;
  const css = {};
  if (char.fontFamily) css.fontFamily = /\s/.test(char.fontFamily) ? `"${char.fontFamily}"` : char.fontFamily;
  if (char.fontSize) css.fontSize = `${char.fontSize}px`;
  if (char.color) css.color = char.color;
  if (char.letterSpacing) css.letterSpacing = char.letterSpacing;
  if (char.textTransform) css.textTransform = char.textTransform;
  if (char.fontVariant) css.fontVariant = char.fontVariant;
  if (char.backgroundColor) css.backgroundColor = char.backgroundColor;
  if (Object.keys(css).length) applyInlineStyle(css);
  const want = {
    bold: char.fontWeight === 'bold' || char.fontWeight === 700 || char.fontWeight === '700',
    italic: char.fontStyle === 'italic',
    underline: /underline/.test(String(char.textDecoration || '')),
    strikeThrough: /line-through/.test(String(char.textDecoration || '')),
  };
  try {
    document.execCommand('styleWithCSS', false, true);
    Object.entries(want).forEach(([cmd, on]) => { if (document.queryCommandState(cmd) !== on) document.execCommand(cmd, false, null); });
    const pos = char.fontVariantPosition;
    const sup = document.queryCommandState('superscript'), sub = document.queryCommandState('subscript');
    if (pos === 'super') { if (sub) document.execCommand('subscript'); if (!sup) document.execCommand('superscript'); }
    else if (pos === 'sub') { if (sup) document.execCommand('superscript'); if (!sub) document.execCommand('subscript'); }
    else { if (sup) document.execCommand('superscript'); if (sub) document.execCommand('subscript'); }
  } catch { /* ignore */ }
  const pcss = paragraphPropsToStyle(para);
  if (Object.keys(pcss).length) applyParagraphStyle(pcss);
  saveSelection();
  editingStore.sync?.();
  return true;
};

/* ---------------- typing style at a bare caret ---------------- */
// Formatting chosen with nothing highlighted and the caret between words is a "typing style": it must land on what is
// typed next, not on the whole box. The browser keeps such a style itself for its own commands (bold, foreColor…) — but
// it is lost as soon as a ribbon input takes the focus, and there is none for an arbitrary px font size. So the typing
// style lives in an empty styled span at the caret holding a zero-width space (the caret can only sit inside a span that
// has a character). The space is removed the moment text is typed (`settleTypingSpans`), the span is dropped again if the
// caret leaves it unused (`pruneTypingSpans`), and neither is ever persisted (`stripTypingArtifacts`).
const TYPING_ATTR = 'data-wp-typing';
const TYPING_SEL = `span[${TYPING_ATTR}]`;

/** The unused typing span (zero-width space only) that `node` sits in, if any. */
const typingSpanAround = (node) => {
  let n = node && node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
  while (n && n !== editingStore.el && editingStore.el?.contains(n)) {
    if (n.tagName === 'SPAN' && n.hasAttribute(TYPING_ATTR) && n.textContent === ZWSP) return n;
    n = n.parentElement;
  }
  return null;
};

/**
 * The browser's pending typing style at the caret (font / bold / italic / colour picked but nothing typed yet) as inline
 * CSS. Changing the selection from script discards that style, so it is copied onto the typing span first.
 */
const pendingTypingCss = (anchorEl) => {
  const css = {};
  if (!anchorEl || document.activeElement !== editingStore.el) return css;
  try {
    const cs = window.getComputedStyle(anchorEl);
    const qf = firstFamily(document.queryCommandValue('fontName'));
    if (qf && qf.toLowerCase() !== firstFamily(cs.fontFamily).toLowerCase()) css.fontFamily = /[\s]/.test(qf) ? `"${qf}"` : qf;
    const bold = document.queryCommandState('bold');
    const w = cs.fontWeight === 'bold' || parseInt(cs.fontWeight, 10) >= 600;
    if (bold !== w) css.fontWeight = bold ? 'bold' : 'normal';
    const italic = document.queryCommandState('italic');
    if (italic !== (cs.fontStyle === 'italic')) css.fontStyle = italic ? 'italic' : 'normal';
    const qc = document.queryCommandValue('foreColor');
    if (qc && qc !== cs.color) css.color = qc;
    // underline / strikethrough stay browser typing styles (turning them *off* inside decorated text needs the browser's
    // splitting); when pending "on", carry them over
    const line = `${cs.textDecorationLine || ''} ${cs.textDecoration || ''}`;
    const dec = [];
    if (document.queryCommandState('underline') && !/underline/.test(line)) dec.push('underline');
    if (document.queryCommandState('strikeThrough') && !/line-through/.test(line)) dec.push('line-through');
    if (dec.length) css.textDecoration = dec.join(' ');
  } catch { /* ignore */ }
  return css;
};

/** Give what is typed next at a bare caret the inline `styles` (see above). Returns false when text is highlighted. */
export const applyTypingStyle = (styles) => {
  if (!isEditingText()) return false;
  restoreSelection();
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount || !sel.isCollapsed) return false;
  const range = sel.getRangeAt(0);
  const root = editingStore.el;
  if (!root.contains(range.startContainer)) return false;
  let span = typingSpanAround(range.startContainer);
  if (!span) {
    const anchorEl = range.startContainer.nodeType === Node.TEXT_NODE ? range.startContainer.parentElement : range.startContainer;
    const pending = pendingTypingCss(anchorEl);
    span = document.createElement('span');
    span.setAttribute(TYPING_ATTR, '1');
    span.appendChild(document.createTextNode(ZWSP));
    range.insertNode(span);
    Object.assign(span.style, pending);
  }
  Object.assign(span.style, styles);
  const r2 = document.createRange();
  r2.setStart(span.firstChild, 1);
  r2.collapse(true);
  sel.removeAllRanges();
  sel.addRange(r2);
  saveSelection();
  editingStore.sync?.();
  return true;
};

/** Remove the zero-width spaces under `el` (caret kept); text nodes / typing spans left empty go too. */
const removeZwsp = (el) => {
  const sel = window.getSelection();
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const nodes = [];
  let n;
  while ((n = walker.nextNode())) if (n.data.includes(ZWSP)) nodes.push(n);
  nodes.forEach((node) => {
    let idx;
    while ((idx = node.data.indexOf(ZWSP)) >= 0) {
      const caretHere = sel && sel.rangeCount && sel.isCollapsed && sel.anchorNode === node;
      const off = caretHere ? sel.anchorOffset : -1;
      node.deleteData(idx, 1);
      if (caretHere) { const r = document.createRange(); r.setStart(node, Math.max(0, off > idx ? off - 1 : off)); r.collapse(true); sel.removeAllRanges(); sel.addRange(r); }
    }
    if (!node.data.length) {
      const parent = node.parentElement;
      node.remove();
      if (parent && parent !== editingStore.el && parent.tagName === 'SPAN' && !parent.childNodes.length) parent.remove();
    }
  });
};

/** Text was typed into a typing span: it is a normal styled span now — drop its zero-width space. */
export const settleTypingSpans = (el) => {
  if (!el || !el.textContent.includes(ZWSP)) return false;
  let changed = false;
  el.querySelectorAll(TYPING_SEL).forEach((span) => {
    if (span.textContent === ZWSP) return; // still waiting for the first character
    span.removeAttribute(TYPING_ATTR);
    removeZwsp(span);
    changed = true;
  });
  return changed;
};

/**
 * The caret moved elsewhere in the box (click, arrow keys) before anything was typed: the unused typing span goes.
 * A selection outside the box (a ribbon input has focus) is not a move — the caret comes back into the span.
 */
export const pruneTypingSpans = (el) => {
  if (!el || !el.querySelector(TYPING_SEL)) return false;
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount || !el.contains(sel.anchorNode)) return false;
  let changed = false;
  el.querySelectorAll(TYPING_SEL).forEach((span) => {
    if (span.textContent !== ZWSP) { span.removeAttribute(TYPING_ATTR); removeZwsp(span); changed = true; return; }
    const inside = sel.isCollapsed && span.contains(sel.anchorNode) && (sel.anchorNode === span ? sel.anchorOffset === 1 : sel.anchorOffset === 1);
    if (!inside) { span.remove(); changed = true; }
  });
  if (changed) saveSelection();
  return changed;
};

/** Backspace right after the zero-width space of an unused typing span: drop the span so the key deletes real text. */
export const dropTypingSpanBeforeBackspace = (el) => {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount || !sel.isCollapsed || !el || !el.contains(sel.anchorNode)) return false;
  const span = typingSpanAround(sel.anchorNode);
  if (!span) return false;
  span.remove();
  saveSelection();
  return true;
};

/** Persisting / leaving edit mode: no zero-width spaces, no typing markers, no empty spans they leave behind. */
export const stripTypingArtifacts = (clone) => {
  if (!clone) return clone;
  clone.querySelectorAll(`[${TYPING_ATTR}]`).forEach((s) => s.removeAttribute(TYPING_ATTR));
  if (!clone.textContent.includes(ZWSP)) return clone;
  const walker = document.createTreeWalker(clone, NodeFilter.SHOW_TEXT);
  const nodes = [];
  let n;
  while ((n = walker.nextNode())) if (n.data.includes(ZWSP)) nodes.push(n);
  nodes.forEach((node) => {
    node.data = node.data.split(ZWSP).join('');
    if (!node.data.length) {
      const parent = node.parentElement;
      node.remove();
      if (parent && parent !== clone && parent.tagName === 'SPAN' && !parent.childNodes.length) parent.remove();
    }
  });
  return clone;
};

const ROOT_BLOCK = /^(DIV|P|UL|OL)$/;
/**
 * Chrome quirk: with the caret at the very start (Ctrl+Home) or end (Ctrl+End) of an editable whose paragraphs are
 * block elements, the caret may sit at the editable root itself and typed text lands *beside* the first / last
 * paragraph as a bare text node — shown as a line of its own, and splitting a linked story's paragraph at the window
 * boundary. Move such text into the neighbouring paragraph (the text node keeps its identity, so the caret stays
 * where it is). Returns true when something moved.
 */
export const absorbRootText = (el) => {
  if (!el) return false;
  let changed = false;
  const sel = window.getSelection();
  const caretAtRoot = !!sel && sel.rangeCount > 0 && sel.isCollapsed && sel.anchorNode === el;
  // Moving a node first removes it, which parks a caret inside it at the root; decide *before* the move whether the
  // caret belongs to this text and put it back after the moved text afterwards.
  const caretFollows = (node) => caretAtRoot || !!(sel && sel.rangeCount && node.contains(sel.anchorNode));
  const caretAfter = (node, follows) => { if (!follows) return; try { const r = document.createRange(); r.setStart(node, node.data.length); r.collapse(true); sel.removeAllRanges(); sel.addRange(r); } catch { /* ignore */ } };
  // helper nodes that are not part of the text (wrap exclusions, see TextContent) are skipped over, never filled
  const skip = (n) => (n.nodeType === Node.TEXT_NODE && !n.data) || (n.nodeType === Node.ELEMENT_NODE && n.hasAttribute('data-wp-exclusion'));
  Array.from(el.childNodes).forEach((node) => {
    if (node.nodeType !== Node.TEXT_NODE || !node.data) return;
    // the paragraph after it (text before the first block) or before it (text after the last block)
    let next = node.nextSibling;
    while (next && skip(next)) next = next.nextSibling;
    let prev = node.previousSibling;
    while (prev && skip(prev)) prev = prev.previousSibling;
    if (next && next.nodeType === Node.ELEMENT_NODE && ROOT_BLOCK.test(next.tagName)) {
      const target = next.tagName === 'UL' || next.tagName === 'OL' ? next.firstElementChild : next;
      if (!target) return;
      if (target.firstChild && target.firstChild.nodeName === 'BR') target.removeChild(target.firstChild);
      const follows = caretFollows(node);
      target.insertBefore(node, target.firstChild);
      caretAfter(node, follows);
      changed = true;
    } else if (prev && prev.nodeType === Node.ELEMENT_NODE && ROOT_BLOCK.test(prev.tagName)) {
      const target = prev.tagName === 'UL' || prev.tagName === 'OL' ? prev.lastElementChild : prev;
      if (!target) return;
      if (target.lastChild && target.lastChild.nodeName === 'BR' && target.childNodes.length === 1) target.removeChild(target.lastChild);
      const follows = caretFollows(node);
      target.appendChild(node);
      caretAfter(node, follows);
      changed = true;
    }
  });
  return changed;
};


export const insertTextAtCaret = (text) => {
  if (!isEditingText()) return false;
  restoreSelection();
  document.execCommand('insertText', false, text);
  saveSelection();
  editingStore.sync?.();
  return true;
};

export const insertHtmlAtCaret = (html) => {
  if (!isEditingText()) return false;
  restoreSelection();
  document.execCommand('insertHTML', false, sanitizeHtml(html));
  saveSelection();
  editingStore.sync?.();
  return true;
};

export const selectAllInEditor = () => {
  if (!isEditingText()) return false;
  const range = document.createRange();
  range.selectNodeContents(editingStore.el);
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(range);
  saveSelection();
  return true;
};

export const getSelectedText = () => {
  if (!isEditingText()) return '';
  const sel = window.getSelection();
  return sel ? sel.toString() : '';
};

export const replaceSelectionText = (text) => insertTextAtCaret(text);

/** Query the current inline state (bold/italic/...) for ribbon toggle display */
export const queryState = (cmd) => {
  if (!isEditingText()) return false;
  try {
    return document.queryCommandState(cmd);
  } catch {
    return false;
  }
};

/* ---------------- text clipboard (Home ▸ Clipboard while editing) ---------------- */
// The ribbon's Cut / Copy / Paste act on the highlighted text while a box is being edited (like Ctrl+C in the box) and
// on whole objects otherwise. The system clipboard is used when the browser allows it; a module copy is kept so Paste
// works even without clipboard permission.
const textClip = { text: '', html: '' };

const selectionHtml = (range) => {
  const div = document.createElement('div');
  div.appendChild(range.cloneContents());
  return div.innerHTML;
};

/** Copy the highlighted text of the edited box. Returns false when nothing is highlighted. */
export const copySelectedText = () => {
  if (!isEditingText()) return false;
  restoreSelection();
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount || sel.isCollapsed || !editingStore.el.contains(sel.anchorNode)) return false;
  const range = sel.getRangeAt(0);
  textClip.text = sel.toString();
  textClip.html = selectionHtml(range);
  try {
    if (navigator.clipboard?.write && typeof window.ClipboardItem === 'function') {
      navigator.clipboard.write([new window.ClipboardItem({ 'text/plain': new Blob([textClip.text], { type: 'text/plain' }), 'text/html': new Blob([textClip.html], { type: 'text/html' }) })]).catch(() => {});
    } else if (navigator.clipboard?.writeText) navigator.clipboard.writeText(textClip.text).catch(() => {});
  } catch { /* clipboard permission denied: the module copy still serves Paste */ }
  return true;
};

/** Cut the highlighted text of the edited box. Returns false when nothing is highlighted. */
export const cutSelectedText = () => {
  if (!copySelectedText()) return false;
  document.execCommand('delete');
  saveSelection();
  editingStore.sync?.();
  return true;
};

/** Paste at the caret of the edited box (system clipboard text when readable, else the last copied text). */
export const pasteTextAtCaret = async () => {
  if (!isEditingText()) return false;
  let text = '';
  try { if (navigator.clipboard?.readText) text = await navigator.clipboard.readText(); } catch { /* no permission */ }
  if (!text) text = textClip.text;
  if (!text) return false;
  return insertTextAtCaret(text);
};

export const hasTextClip = () => !!textClip.text;
